import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * MagicLinkVerify - Handles magic link token verification
 * Route: /auth/verify?token=xxx
 */
function MagicLinkVerify() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyMagicLink } = useAuth();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState('');
  const verifyCalledRef = useRef(false); // Prevent duplicate calls

  useEffect(() => {
    // Only run once - prevent duplicate verify calls
    if (verifyCalledRef.current) return;

    const token = searchParams.get('token');
    console.log('[MagicLinkVerify] Token from URL:', token ? token.substring(0, 16) + '...' : 'MISSING');

    if (!token) {
      setStatus('error');
      setError('Invalid or missing magic link token. Please request a new login link.');
      return;
    }

    verifyCalledRef.current = true;

    const verify = async () => {
      try {
        const userData = await verifyMagicLink(token);
        console.log('[MagicLinkVerify] Verification successful - user:', userData?.email, 'role:', userData?.role);
        setStatus('success');

        // Redirect based on role after brief delay
        setTimeout(() => {
          const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
          if (redirectUrl) {
            console.log('[MagicLinkVerify] Redirecting to stored URL:', redirectUrl);
            sessionStorage.removeItem('redirectAfterLogin');
            navigate(redirectUrl, { replace: true });
            return;
          }

          switch (userData?.role) {
            case 'admin':
              navigate('/admin', { replace: true });
              break;
            case 'recruiter':
              navigate('/recruiter/assessments', { replace: true });
              break;
            case 'interviewer':
              navigate('/interviewer/stats', { replace: true });
              break;
            case 'candidate':
              navigate('/candidate', { replace: true });
              break;
            case 'trainer':
              navigate('/training', { replace: true });
              break;
            default:
              navigate('/', { replace: true });
          }
        }, 500);
      } catch (err) {
        console.error('[MagicLinkVerify] Verification failed:', err.message);
        setStatus('error');
        setError(err.message || 'Invalid or expired magic link. Please request a new one.');
      }
    };

    verify();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: '#f8f9fa',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '48px',
        maxWidth: '440px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
      }}>
        {status === 'verifying' && (
          <>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #e5e7eb',
              borderTopColor: '#2196f3',
              borderRadius: '50%',
              margin: '0 auto 24px',
              animation: 'spin 0.8s linear infinite'
            }} />
            <h2 style={{ fontSize: '20px', color: '#212529', marginBottom: '8px' }}>
              Signing you in...
            </h2>
            <p style={{ color: '#6c757d', fontSize: '15px' }}>
              Verifying your magic link
            </p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: '#d4edda',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: '28px',
              color: '#28a745'
            }}>
              ✓
            </div>
            <h2 style={{ fontSize: '20px', color: '#212529', marginBottom: '8px' }}>
              Welcome back!
            </h2>
            <p style={{ color: '#6c757d', fontSize: '15px' }}>
              Redirecting to your dashboard...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: '#f8d7da',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: '28px',
              color: '#dc3545'
            }}>
              ✗
            </div>
            <h2 style={{ fontSize: '20px', color: '#212529', marginBottom: '12px' }}>
              Sign In Failed
            </h2>
            <p style={{ color: '#6c757d', fontSize: '15px', marginBottom: '24px' }}>
              {error}
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              style={{
                background: '#2196f3',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 32px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default MagicLinkVerify;
